import React, { useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { TimeoutDialog } from './TimeoutDialog';

type MESSAGE_TYPE = 'LOGOUT' | 'STAY';

interface TimeoutWrapperProps {
  idleTimeout?: Seconds;
  promptTimeout?: Seconds;
  actionThrottle?: Seconds;
  onLogoutRequest(): void;
}

const IDLE_TIMER_NAME = 'ocTimeoutWrapper';

export const TimeoutWrapper: React.FC<React.PropsWithChildren<TimeoutWrapperProps>> = ({
  idleTimeout = 300,
  promptTimeout = 30,
  actionThrottle = 3,
  onLogoutRequest
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const idleTimoutMs = toMilliseconds(idleTimeout);
  const promptTimeoutMs = toMilliseconds(promptTimeout);
  const actionThrottleMs = toMilliseconds(actionThrottle);

  const handleMessageFromOtherTabs = (data: MESSAGE_TYPE) => {
    if (data === 'LOGOUT') {
      onLogoutRequest();
    }
    if (data === 'STAY') {
      // close the prompt in other tabs if it's open
      // Session timeout is already synchronized.
      setShowModal(false);
    }
  };

  // sends a message to other tabs
  const sendMessageToOtherTabs = (msg: MESSAGE_TYPE) => {
    message(msg);
  };

  // handleIdle is called when onPrompt timeout is reached
  const handleIdle = () => {
    handleDialogLogout();
  };

  const handleDialogLogout = () => {
    sendMessageToOtherTabs('LOGOUT');
    onLogoutRequest();
  };

  const handleDialogCancelLogout = () => {
    setShowModal(false);
    sendMessageToOtherTabs('STAY');
    reset();
  };

  // Show prompt when onPrompt is called. promptTimeout prop defines extra timeout.
  // If there is no action performed by user in this prompt then onIdle (handleIdle) callback is executed
  const handlePrompt = () => {
    setShowModal(true);
  };

  // extends session timeout if user has performed any action such as click, scroll etc. Reset is synchronized across tabs.
  const handleAction = () => {
    reset();
  };

  const { reset, message } = useIdleTimer({
    timeout: idleTimoutMs,
    crossTab: true,
    name: IDLE_TIMER_NAME,
    throttle: actionThrottleMs,
    onIdle: handleIdle,
    onMessage: handleMessageFromOtherTabs,
    onPrompt: handlePrompt,
    onAction: handleAction,
    promptTimeout: promptTimeoutMs
  });

  return showModal ? (
    <TimeoutDialog
      promptTimeout={promptTimeoutMs}
      handleLogout={handleDialogLogout}
      handleCancelLogout={handleDialogCancelLogout}
    />
  ) : null;
};

function toMilliseconds(seconds: Seconds): Milliseconds {
  return seconds * 1000;
}
